/**
 * External dependencies
 */
import clsx from 'clsx';
/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { unseen, funnel } from '@wordpress/icons';
import { Button, Icon, privateApis as componentsPrivateApis, CheckboxControl, Spinner } from '@wordpress/components';
import { forwardRef, useEffect, useId, useRef, useState, useMemo, Children, Fragment } from '@wordpress/element';

/**
 * Internal dependencies
 */
import SingleSelectionCheckbox from './single-selection-checkbox';
import { unlock } from './lock-unlock';
import ItemActions from './item-actions';
import { sanitizeOperators } from './utils';
import { SORTING_DIRECTIONS, sortArrows, sortLabels, sortValues } from './constants';
import { useSomeItemHasAPossibleBulkAction, useHasAPossibleBulkAction } from './bulk-actions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const {
  DropdownMenuV2: DropdownMenu,
  DropdownMenuGroupV2: DropdownMenuGroup,
  DropdownMenuItemV2: DropdownMenuItem,
  DropdownMenuRadioItemV2: DropdownMenuRadioItem,
  DropdownMenuItemLabelV2: DropdownMenuItemLabel,
  DropdownMenuSeparatorV2: DropdownMenuSeparator
} = unlock(componentsPrivateApis);
function WithDropDownMenuSeparators({
  children
}) {
  return Children.toArray(children).filter(Boolean).map((child, i) => /*#__PURE__*/_jsxs(Fragment, {
    children: [i > 0 && /*#__PURE__*/_jsx(DropdownMenuSeparator, {}), child]
  }, i));
}
const _HeaderMenu = forwardRef(function HeaderMenu({
  field,
  view,
  fields,
  onChangeView,
  onHide,
  setOpenedFilter
}, ref) {
  const isHidable = field.enableHiding !== false;
  const isSortable = field.enableSorting !== false;
  const isSorted = view.sort?.field === field.id;
  const operators = sanitizeOperators(field);
  // Filter can be added:
  // 1. If the field is not already part of a view's filters.
  // 2. If the field meets the type and operator requirements.
  // 3. If it's not primary. If it is, it should be already visible.
  const canAddFilter = !view.filters?.some(_filter => field.id === _filter.field) && !!field.elements?.length && !!operators.length && !field.filterBy?.isPrimary;
  if (!isSortable && !isHidable && !canAddFilter) {
    return field.header;
  }
  return /*#__PURE__*/_jsx(DropdownMenu, {
    align: "start",
    trigger: /*#__PURE__*/_jsxs(Button, {
      size: "compact",
      className: "dataviews-view-table-header-button",
      ref: ref,
      variant: "tertiary",
      children: [field.header, view.sort && isSorted && /*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        children: sortArrows[view.sort.direction]
      })]
    }),
    style: {
      minWidth: '240px'
    },
    children: /*#__PURE__*/_jsxs(WithDropDownMenuSeparators, {
      children: [isSortable && /*#__PURE__*/_jsx(DropdownMenuGroup, {
        children: SORTING_DIRECTIONS.map(direction => {
          const isChecked = view.sort && isSorted && view.sort.direction === direction;
          const value = `${field.id}-${direction}`;
          return /*#__PURE__*/_jsx(DropdownMenuRadioItem, {
            // All sorting radio items share the same name, so that
            // selecting a sorting option automatically deselects the
            // previously selected one, even if it is displayed in
            // another submenu. The field and direction are passed via
            // the `value` prop.
            name: "view-table-sorting",
            value: value,
            checked: isChecked,
            onChange: () => {
              onChangeView({
                ...view,
                sort: {
                  field: field.id,
                  direction
                }
              });
            },
            children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
              children: sortLabels[direction]
            })
          }, value);
        })
      }), canAddFilter && /*#__PURE__*/_jsx(DropdownMenuGroup, {
        children: /*#__PURE__*/_jsx(DropdownMenuItem, {
          prefix: /*#__PURE__*/_jsx(Icon, {
            icon: funnel
          }),
          onClick: () => {
            setOpenedFilter(field.id);
            onChangeView({
              ...view,
              page: 1,
              filters: [...(view.filters || []), {
                field: field.id,
                value: undefined,
                operator: operators[0]
              }]
            });
          },
          children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
            children: __('Add filter')
          })
        })
      }), isHidable && /*#__PURE__*/_jsx(DropdownMenuItem, {
        prefix: /*#__PURE__*/_jsx(Icon, {
          icon: unseen
        }),
        onClick: () => {
          const viewFields = view.fields || fields.map(f => f.id);
          onHide(field);
          onChangeView({
            ...view,
            fields: viewFields.filter(fieldId => fieldId !== field.id)
          });
        },
        children: /*#__PURE__*/_jsx(DropdownMenuItemLabel, {
          children: __('Hide')
        })
      })]
    })
  });
});

// @ts-expect-error Lift the `Item` type argument through the forwardRef.
const HeaderMenu = _HeaderMenu;
function BulkSelectionCheckbox({
  selection,
  onSelectionChange,
  data,
  actions,
  getItemId
}) {
  const selectableItems = useMemo(() => {
    return data.filter(item => {
      return actions.some(action => action.supportsBulk && (!action.isEligible || action.isEligible(item)));
    });
  }, [data, actions]);
  const selectedItems = data.filter(item => selection.includes(getItemId(item)) && selectableItems.includes(item));
  const areAllSelected = selectedItems.length === selectableItems.length;
  return /*#__PURE__*/_jsx(CheckboxControl, {
    className: "dataviews-view-table-selection-checkbox",
    __nextHasNoMarginBottom: true,
    checked: areAllSelected,
    indeterminate: !areAllSelected && !!selectedItems.length,
    onChange: () => {
      if (areAllSelected) {
        onSelectionChange([]);
      } else {
        onSelectionChange(selectableItems.map(item => getItemId(item)));
      }
    },
    "aria-label": areAllSelected ? __('Deselect all') : __('Select all')
  });
}
function TableRow({
  hasBulkActions,
  item,
  actions,
  id,
  visibleFields,
  primaryField,
  selection,
  getItemId,
  onSelectionChange
}) {
  const hasPossibleBulkAction = useHasAPossibleBulkAction(actions, item);
  const isSelected = hasPossibleBulkAction && selection.includes(id);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Will be set to true if `onTouchStart` fires. This happens before
  // `onClick` and can be used to exclude touchscreen devices from certain
  // behaviours.
  const isTouchDevice = useRef(false);
  return /*#__PURE__*/_jsxs("tr", {
    className: clsx('dataviews-view-table__row', {
      'is-selected': hasPossibleBulkAction && isSelected,
      'is-hovered': isHovered,
      'has-bulk-actions': hasPossibleBulkAction
    }),
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onTouchStart: () => {
      isTouchDevice.current = true;
    },
    onClick: () => {
      if (!hasPossibleBulkAction) {
        return;
      }
      if (!isTouchDevice.current && document.getSelection()?.type !== 'Range') {
        onSelectionChange(selection.includes(id) ? selection.filter(itemId => id !== itemId) : [...selection, id]);
      }
    },
    children: [hasBulkActions && /*#__PURE__*/_jsx("td", {
      className: "dataviews-view-table__checkbox-column",
      style: {
        width: '1%'
      },
      children: /*#__PURE__*/_jsx("div", {
        className: "dataviews-view-table__cell-content-wrapper",
        children: /*#__PURE__*/_jsx(SingleSelectionCheckbox, {
          item: item,
          selection: selection,
          onSelectionChange: onSelectionChange,
          getItemId: getItemId,
          primaryField: primaryField,
          disabled: !hasPossibleBulkAction
        })
      })
    }), visibleFields.map(field => /*#__PURE__*/_jsx("td", {
      style: {
        width: field.width || undefined,
        minWidth: field.minWidth || undefined,
        maxWidth: field.maxWidth || undefined
      },
      children: /*#__PURE__*/_jsx("div", {
        className: clsx('dataviews-view-table__cell-content-wrapper', {
          'dataviews-view-table__primary-field': primaryField?.id === field.id
        }),
        children: field.render({
          item
        })
      })
    }, field.id)), !!actions?.length &&
    /*#__PURE__*/
    // Disable reason: we are not making the element interactive,
    // but preventing any click events from bubbling up to the
    // table row. This allows us to add a click handler to the row
    // itself (to toggle row selection) without erroneously
    // intercepting click events from ItemActions.
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
    _jsx("td", {
      className: "dataviews-view-table__actions-column",
      onClick: e => e.stopPropagation(),
      children: /*#__PURE__*/_jsx(ItemActions, {
        item: item,
        actions: actions
      })
    })
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */]
  });
}
function ViewTable({
  actions,
  data,
  fields,
  getItemId,
  isLoading = false,
  onChangeView,
  onSelectionChange,
  selection,
  setOpenedFilter,
  view
}) {
  const headerMenuRefs = useRef(new Map());
  const headerMenuToFocusRef = useRef();
  const [nextHeaderMenuToFocus, setNextHeaderMenuToFocus] = useState();
  const hasBulkActions = useSomeItemHasAPossibleBulkAction(actions, data);
  useEffect(() => {
    if (headerMenuToFocusRef.current) {
      headerMenuToFocusRef.current.focus();
      headerMenuToFocusRef.current = undefined;
    }
  });
  const tableNoticeId = useId();
  if (nextHeaderMenuToFocus) {
    // If we need to force focus, we short-circuit rendering here
    // to prevent any additional work while we handle that.
    // Clearing out the focus directive is necessary to make sure
    // future renders don't cause unexpected focus jumps.
    headerMenuToFocusRef.current = nextHeaderMenuToFocus;
    setNextHeaderMenuToFocus(undefined);
    return;
  }
  const onHide = field => {
    const hidden = headerMenuRefs.current.get(field.id);
    const fallback = hidden ? headerMenuRefs.current.get(hidden.fallback) : undefined;
    setNextHeaderMenuToFocus(fallback?.node);
  };
  const viewFields = view.fields || fields.map(f => f.id);
  const visibleFields = fields.filter(field => viewFields.includes(field.id) || [view.layout?.mediaField].includes(field.id));
  const hasData = !!data?.length;
  const primaryField = fields.find(field => field.id === view.layout?.primaryField);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("table", {
      className: "dataviews-view-table",
      "aria-busy": isLoading,
      "aria-describedby": tableNoticeId,
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs("tr", {
          className: "dataviews-view-table__row",
          children: [hasBulkActions && /*#__PURE__*/_jsx("th", {
            className: "dataviews-view-table__checkbox-column",
            style: {
              width: '1%'
            },
            "data-field-id": "selection",
            scope: "col",
            children: /*#__PURE__*/_jsx(BulkSelectionCheckbox, {
              selection: selection,
              onSelectionChange: onSelectionChange,
              data: data,
              actions: actions,
              getItemId: getItemId
            })
          }), visibleFields.map((field, index) => /*#__PURE__*/_jsx("th", {
            style: {
              width: field.width || undefined,
              minWidth: field.minWidth || undefined,
              maxWidth: field.maxWidth || undefined
            },
            "data-field-id": field.id,
            "aria-sort": view.sort?.field === field.id ? sortValues[view.sort.direction] : undefined,
            scope: "col",
            children: /*#__PURE__*/_jsx(HeaderMenu, {
              ref: node => {
                if (node) {
                  headerMenuRefs.current.set(field.id, {
                    node,
                    fallback: visibleFields[index > 0 ? index - 1 : 1]?.id
                  });
                } else {
                  headerMenuRefs.current.delete(field.id);
                }
              },
              field: field,
              view: view,
              fields: fields,
              onChangeView: onChangeView,
              onHide: onHide,
              setOpenedFilter: setOpenedFilter
            })
          }, field.id)), !!actions?.length && /*#__PURE__*/_jsx("th", {
            "data-field-id": "actions",
            className: "dataviews-view-table__actions-column",
            children: /*#__PURE__*/_jsx("span", {
              className: "dataviews-view-table-header",
              children: __('Actions')
            })
          })]
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: hasData && data.map((item, index) => /*#__PURE__*/_jsx(TableRow, {
          item: item,
          hasBulkActions: hasBulkActions,
          actions: actions,
          id: getItemId(item) || index.toString(),
          visibleFields: visibleFields,
          primaryField: primaryField,
          selection: selection,
          getItemId: getItemId,
          onSelectionChange: onSelectionChange
        }, getItemId(item)))
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: clsx({
        'dataviews-loading': isLoading,
        'dataviews-no-results': !hasData && !isLoading
      }),
      id: tableNoticeId,
      children: !hasData && /*#__PURE__*/_jsx("p", {
        children: isLoading ? /*#__PURE__*/_jsx(Spinner, {}) : __('No results')
      })
    })]
  });
}
export default ViewTable;
//# sourceMappingURL=view-table.js.map